import { refreshSessionIfNeeded } from "./auth-service";
import { clearSession } from "./session-store";

/**
 * Custom error thrown when authentication is required but unavailable.
 */
export class AuthRequiredError extends Error {
  constructor(message = "Authentication required") {
    super(message);
    this.name = "AuthRequiredError";
  }
}

/**
 * Fetches a resource with automatic token refresh and retry on 401.
 * 
 * Workflow:
 * 1. Loads/refreshes session if needed
 * 2. Adds Authorization header with access token
 * 3. Performs fetch
 * 4. If 401, forces a token refresh and retries once
 * 5. If still 401, clears session and throws AuthRequiredError
 * 
 * @param input - Request URL or Request object
 * @param init - Optional RequestInit configuration
 * @returns Response from the fetch
 * @throws AuthRequiredError if authentication fails or is unavailable
 */
export async function fetchWithAuthRetry(
  input: RequestInfo,
  init?: RequestInit
): Promise<Response> {
  // First attempt: load or refresh session if needed
  let session = await refreshSessionIfNeeded();
  
  if (!session) {
    throw new AuthRequiredError("No valid session available");
  }

  // Prepare request with authorization header
  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${session.access_token}`);

  const requestInit: RequestInit = {
    ...init,
    headers,
  };

  // First attempt
  let response = await fetch(input, requestInit);

  // If 401, try to force refresh and retry once
  if (response.status === 401) {
    console.log("[AuthenticatedFetch] Received 401, forcing token refresh");
    
    // Force refresh the session
    session = await refreshSessionIfNeeded({ force: true });
    
    if (!session) {
      // Refresh failed - clear session and throw
      await clearSession();
      throw new AuthRequiredError("Session refresh failed after 401");
    }

    // Update authorization header with new token
    headers.set("Authorization", `Bearer ${session.access_token}`);
    const retryInit: RequestInit = {
      ...init,
      headers,
    };

    // Retry the request
    response = await fetch(input, retryInit);

    // If still 401, clear session and fail
    if (response.status === 401) {
      console.error("[AuthenticatedFetch] Still 401 after refresh, clearing session");
      await clearSession();
      throw new AuthRequiredError("Authentication failed after retry");
    }
  }

  return response;
}
